(() => {
  'use strict';

  if (window.PolyPouchPurchasingAuthority) return;

  const VERSION = '1.0.2';
  const OFFER_ENDPOINT = /\/api\/v2\/shop\/get_offer_table(?:[/?#]|$)/i;
  const HULK_CONTROL = '#card-list-button, .offer-option-btn';
  const HULK_SELECTED = 'input[name="card_group"]:checked';
  const NATIVE_FORM = 'product-form form[data-type="add-to-cart-form"]';
  const NATIVE_CONTROL = '.product-form__submit, [type="submit"]';
  const NATIVE_QUANTITY = 'quantity-input input[name="quantity"], input[name="quantity"]';
  const VARIANT_CONTROL = 'variant-selects select, variant-radios input[type="radio"]';
  const RELEVANT_CHANGE = `${HULK_SELECTED}, ${NATIVE_QUANTITY}, ${VARIANT_CONTROL}`;
  const listeners = new Set();
  const elementIds = new WeakMap();

  let elementSequence = 0;
  let revision = 0;
  let epoch = 0;
  let requestSequence = 0;
  let latestRequest = 0;
  let settleTimer = null;
  let resolveFrame = null;
  let initialized = false;
  let variantInvalidated = false;
  let structureObserver = null;
  let stateObserver = null;
  let observedElements = [];
  let network = Object.freeze({ status: 'idle', statusCode: null, requestId: null, epoch: 0 });
  let snapshot = Object.freeze({
    provider: 'pending',
    authority: null,
    ready: false,
    variantId: null,
    variantLabel: '',
    quantity: null,
    unitPriceText: '',
    totalPriceText: '',
    control: null,
    form: null,
    quantityInput: null,
    focusTarget: null,
    optionRoot: null,
    multiple: false,
    disabled: true,
    disabledReason: '',
    offerCount: 0,
    selectedOfferKey: null,
    revision: 0,
    epoch: 0,
    provenance: Object.freeze({ reason: 'bootstrap', offerRequest: 'idle', offerStatusCode: null }),
  });
  let signature = '';

  const text = (element) => element?.textContent?.replace(/\s+/g, ' ').trim() || '';
  const isDisabled = (element) =>
    !element || element.disabled || element.getAttribute('aria-disabled') === 'true';
  const isVisible = (element) => {
    if (!element || element.hidden) return false;
    const style = window.getComputedStyle(element);
    return style.display !== 'none' && style.visibility !== 'hidden' && element.getClientRects().length > 0;
  };
  const elementId = (element) => {
    if (!element) return null;
    if (!elementIds.has(element)) elementIds.set(element, ++elementSequence);
    return elementIds.get(element);
  };
  const parseQuantity = (...values) => {
    for (const value of values) {
      const normalized = String(value ?? '').replace(/,/g, '').trim();
      if (!/^\d+(?:\.\d+)?$/.test(normalized)) continue;
      const numeric = Number(normalized);
      if (Number.isFinite(numeric) && numeric > 0) return numeric;
    }
    return null;
  };
  const productRoot = () => document.querySelector('#MainContent .product');
  const selectedVariantLabel = (root) => {
    if (!root) return '';
    const selects = Array.from(root.querySelectorAll('variant-selects select'))
      .map((select) => text(select.selectedOptions[0]))
      .filter((value) => value && value.toLowerCase() !== 'default title');
    if (selects.length) return selects.join(' / ');

    return Array.from(root.querySelectorAll('variant-radios input[type="radio"]:checked'))
      .map((input) => text(root.querySelector(`label[for="${CSS.escape(input.id)}"]`)) || input.value)
      .filter((value) => value && value.toLowerCase() !== 'default title')
      .join(' / ');
  };
  const nativePrice = (root) => {
    const candidates = [
      '.product__info-container .price__sale .price-item--sale',
      '.product__info-container .price__regular .price-item--regular',
      '.product__info-container .price-item--regular',
    ];
    return text(candidates.map((selector) => root?.querySelector(selector)).find(isVisible));
  };
  const nativeElements = (root) => {
    const forms = Array.from(document.querySelectorAll(NATIVE_FORM));
    const form = root?.querySelector(NATIVE_FORM) || forms.find((candidate) => isVisible(candidate.closest('product-form')));
    const control = form?.querySelector(NATIVE_CONTROL) || null;
    const quantityInput = root?.querySelector(NATIVE_QUANTITY) || null;
    const variantInput = form?.querySelector('input[name="id"]') || null;
    return { form, control, quantityInput, variantInput };
  };
  const disabledReason = (control) => text(control) || control?.getAttribute('aria-label') || '';

  const syncHulkVariantAvailability = (control, nativeControl) => {
    if (!control || !nativeControl) return;

    const unavailable = isDisabled(nativeControl);
    const managed = control.getAttribute('data-pp-variant-unavailable') === 'true';

    if (unavailable) {
      if (!managed) {
        control.setAttribute(
          'data-pp-available-label',
          text(control) || window.variantStrings?.addToCart || 'Add to cart',
        );
        control.setAttribute('data-pp-prior-disabled', control.disabled ? 'true' : 'false');
        const priorAriaDisabled = control.getAttribute('aria-disabled');
        if (priorAriaDisabled !== null) {
          control.setAttribute('data-pp-prior-aria-disabled', priorAriaDisabled);
        }
      }

      if (!control.disabled) control.disabled = true;
      if (control.getAttribute('aria-disabled') !== 'true') control.setAttribute('aria-disabled', 'true');
      if (!managed) control.setAttribute('data-pp-variant-unavailable', 'true');

      const soldOutLabel = window.variantStrings?.soldOut || 'Sold out';
      if (text(control) !== soldOutLabel) control.textContent = soldOutLabel;
      return;
    }

    if (!managed) return;

    control.disabled = control.getAttribute('data-pp-prior-disabled') === 'true';
    if (control.hasAttribute('data-pp-prior-aria-disabled')) {
      control.setAttribute('aria-disabled', control.getAttribute('data-pp-prior-aria-disabled'));
    } else {
      control.removeAttribute('aria-disabled');
    }

    const availableLabel = control.getAttribute('data-pp-available-label') || window.variantStrings?.addToCart || 'Add to cart';
    if (text(control) !== availableLabel) control.textContent = availableLabel;
    control.removeAttribute('data-pp-variant-unavailable');
    control.removeAttribute('data-pp-available-label');
    control.removeAttribute('data-pp-prior-disabled');
    control.removeAttribute('data-pp-prior-aria-disabled');
  };

  const makeSignature = (next) =>
    JSON.stringify({
      provider: next.provider,
      authority: next.authority,
      variantId: next.variantId,
      variantLabel: next.variantLabel,
      quantity: next.quantity,
      unitPriceText: next.unitPriceText,
      totalPriceText: next.totalPriceText,
      disabled: next.disabled,
      disabledReason: next.disabledReason,
      offerCount: next.offerCount,
      selectedOfferKey: next.selectedOfferKey,
      control: elementId(next.control),
      form: elementId(next.form),
      quantityInput: elementId(next.quantityInput),
      epoch: next.epoch,
      reason: next.provenance.reason,
      offerRequest: next.provenance.offerRequest,
      offerStatusCode: next.provenance.offerStatusCode,
    });

  const emit = () => {
    listeners.forEach((listener) => {
      try {
        listener(snapshot);
      } catch (error) {
        window.setTimeout(() => console.error('[purchasing-authority] subscriber failed', error));
      }
    });
    document.dispatchEvent(new CustomEvent('polypouch:purchasing-authority-change', { detail: snapshot }));
  };

  const publish = (next) => {
    const nextSignature = makeSignature(next);
    if (nextSignature === signature) return snapshot;
    signature = nextSignature;
    revision += 1;
    snapshot = Object.freeze({ ...next, revision });
    emit();
    return snapshot;
  };

  const baseState = (reason, root, native) => ({
    provider: 'pending',
    authority: null,
    ready: false,
    variantId: native.variantInput?.value || null,
    variantLabel: selectedVariantLabel(root),
    quantity: null,
    unitPriceText: '',
    totalPriceText: '',
    control: null,
    form: null,
    quantityInput: null,
    focusTarget: null,
    optionRoot: null,
    multiple: false,
    disabled: true,
    disabledReason: '',
    offerCount: 0,
    selectedOfferKey: null,
    revision,
    epoch,
    provenance: Object.freeze({
      reason,
      offerRequest: network.status,
      offerStatusCode: network.statusCode,
    }),
  });

  const observeStateElements = (elements) => {
    const next = elements.filter(Boolean);
    if (
      next.length === observedElements.length &&
      next.every((element, index) => element === observedElements[index])
    ) return;

    stateObserver?.disconnect();
    observedElements = next;
    if (!next.length) return;

    stateObserver = new MutationObserver(() => scheduleResolve('authoritative-dom-change'));
    next.forEach((element) => {
      stateObserver.observe(element, {
        childList: true,
        subtree: true,
        characterData: true,
        attributes: true,
        attributeFilter: ['checked', 'disabled', 'aria-disabled', 'class', 'hidden', 'value'],
      });
    });
  };

  const resolve = (reason = 'resolve') => {
    resolveFrame = null;
    if (!initialized) return snapshot;
    const root = productRoot();
    const native = nativeElements(root);
    const offerRoot = root?.querySelector('.hulkapps-volumes') || null;
    const selected = offerRoot?.querySelector(HULK_SELECTED) || null;
    const control = offerRoot?.querySelector(HULK_CONTROL) || null;
    const card = selected?.closest('.offer-option-item') || selected?.parentElement || null;
    const options = offerRoot ? Array.from(offerRoot.querySelectorAll('input[name="card_group"]')) : [];
    const priceRoot = root?.querySelector('.product__info-container [id^="price-"]') || null;

    syncHulkVariantAvailability(control, native.control);
    observeStateElements([offerRoot, native.form, native.control, native.quantityInput, priceRoot]);
    const base = baseState(reason, root, native);
    const quantity = parseQuantity(selected?.value, text(card?.querySelector('.value')));
    const hulkRendered = Boolean(
      !variantInvalidated && offerRoot && isVisible(offerRoot) && selected && control && quantity,
    );

    if (hulkRendered) {
      const disabled = isDisabled(control) || isDisabled(native.control);
      return publish({
        ...base,
        provider: disabled ? 'unavailable' : 'hulk',
        authority: 'hulk',
        ready: true,
        quantity,
        unitPriceText: text(card?.querySelector('.item-regular_price .money')),
        totalPriceText: text(card?.querySelector('.total-regular_price .money')),
        control,
        focusTarget: card,
        optionRoot: offerRoot,
        multiple: options.length > 1,
        disabled,
        disabledReason: disabled ? disabledReason(control) || disabledReason(native.control) : '',
        offerCount: options.length,
        selectedOfferKey: selected.value || null,
      });
    }

    const partialHulk = Boolean(offerRoot && isVisible(offerRoot) && (selected || control || options.length));
    if (network.status === 'success' && partialHulk) {
      return publish({
        ...base,
        provider: 'error',
        disabledReason: 'Hulk offer table is incomplete for the selected variant',
      });
    }

    if (
      (network.status === 'success' || network.status === 'error') &&
      native.form &&
      native.control &&
      native.quantityInput
    ) {
      const nativeQuantity = parseQuantity(native.quantityInput.value);
      if (!nativeQuantity) {
        return publish({
          ...base,
          provider: 'error',
          disabledReason: 'Native quantity is missing or invalid',
        });
      }
      const disabled = isDisabled(native.control);
      const price = nativePrice(root);
      return publish({
        ...base,
        provider: disabled ? 'unavailable' : 'native',
        authority: 'native',
        ready: true,
        quantity: nativeQuantity,
        unitPriceText: price,
        totalPriceText: price,
        control: native.control,
        form: native.form,
        quantityInput: native.quantityInput,
        focusTarget: native.quantityInput,
        optionRoot: native.quantityInput.closest('quantity-input') || native.quantityInput,
        disabled,
        disabledReason: disabled ? disabledReason(native.control) : '',
      });
    }

    if (network.status === 'error') {
      return publish({
        ...base,
        provider: 'error',
        disabledReason: `Hulk offer request failed${network.statusCode ? ` (${network.statusCode})` : ''}`,
      });
    }

    if (network.status === 'success') {
      return publish({
        ...base,
        provider: 'error',
        disabledReason: 'No complete Hulk or native purchasing control was rendered',
      });
    }

    return publish(base);
  };

  function scheduleResolve(reason) {
    if (resolveFrame) return;
    resolveFrame = window.requestAnimationFrame(() => resolve(reason));
  }

  const setNetwork = (status, statusCode, requestId, requestEpoch) => {
    network = Object.freeze({ status, statusCode, requestId, epoch: requestEpoch });
    scheduleResolve(`offer-request-${status}`);
  };

  const beginOfferRequest = () => {
    window.clearTimeout(settleTimer);
    const request = { id: ++requestSequence, epoch };
    latestRequest = request.id;
    setNetwork('pending', null, request.id, request.epoch);
    return request;
  };

  const finishOfferRequest = (request, statusCode) => {
    if (!request || request.id !== latestRequest || request.epoch !== epoch) return;
    window.clearTimeout(settleTimer);
    if (!(statusCode >= 200 && statusCode < 300)) {
      setNetwork('error', statusCode || null, request.id, request.epoch);
      return;
    }

    variantInvalidated = false;
    setNetwork('settling', statusCode, request.id, request.epoch);
    settleTimer = window.setTimeout(() => {
      if (request.id !== latestRequest || request.epoch !== epoch) return;
      setNetwork('success', statusCode, request.id, request.epoch);
    }, 300);
  };

  const requestUrl = (input) => {
    if (typeof input === 'string') return input;
    if (input instanceof URL) return input.href;
    return input?.url || '';
  };

  if (typeof window.fetch === 'function') {
    const originalFetch = window.fetch;
    window.fetch = function purchasingAuthorityFetch(input, init) {
      const tracked = OFFER_ENDPOINT.test(requestUrl(input));
      const request = tracked ? beginOfferRequest() : null;
      try {
        const result = originalFetch.call(this, input, init);
        if (!tracked) return result;
        return result.then(
          (response) => {
            finishOfferRequest(request, response.status);
            return response;
          },
          (error) => {
            finishOfferRequest(request, 0);
            throw error;
          },
        );
      } catch (error) {
        if (tracked) finishOfferRequest(request, 0);
        throw error;
      }
    };
  }

  if (window.XMLHttpRequest) {
    const originalOpen = XMLHttpRequest.prototype.open;
    const originalSend = XMLHttpRequest.prototype.send;
    XMLHttpRequest.prototype.open = function purchasingAuthorityOpen(method, url, ...rest) {
      this.__polyPouchOfferTracked = OFFER_ENDPOINT.test(requestUrl(url));
      return originalOpen.call(this, method, url, ...rest);
    };
    XMLHttpRequest.prototype.send = function purchasingAuthoritySend(body) {
      const request = this.__polyPouchOfferTracked ? beginOfferRequest() : null;
      if (request) {
        this.addEventListener('loadend', () => finishOfferRequest(request, this.status), { once: true });
      }
      return originalSend.call(this, body);
    };
  }

  const invalidateVariant = () => {
    epoch += 1;
    variantInvalidated = true;
    latestRequest = 0;
    window.clearTimeout(settleTimer);
    network = Object.freeze({ status: 'idle', statusCode: null, requestId: null, epoch });
    const root = productRoot();
    publish(baseState('variant-change', root, nativeElements(root)));
  };

  const handleChange = (event) => {
    if (!event.target.matches(RELEVANT_CHANGE)) return;
    if (event.target.matches(VARIANT_CONTROL)) invalidateVariant();
    else scheduleResolve('selection-change');
  };
  const handleClick = (event) => {
    if (event.target.closest('.offer-option-item')) window.setTimeout(() => scheduleResolve('offer-click'), 0);
  };
  const handleSectionLifecycle = () => scheduleResolve('shopify-section-lifecycle');

  const init = () => {
    if (initialized) return;
    initialized = true;
    const main = document.querySelector('#MainContent');
    if (main) {
      structureObserver = new MutationObserver(() => scheduleResolve('structure-change'));
      structureObserver.observe(main, { childList: true, subtree: true });
    }
    document.addEventListener('change', handleChange, true);
    document.addEventListener('click', handleClick);
    document.addEventListener('shopify:section:load', handleSectionLifecycle);
    document.addEventListener('shopify:section:unload', handleSectionLifecycle);
    scheduleResolve('initial-dom');
  };

  const subscribe = (listener, options = {}) => {
    if (typeof listener !== 'function') throw new TypeError('Purchasing authority subscriber must be a function');
    listeners.add(listener);
    if (options.emitCurrent !== false) listener(snapshot);
    return () => listeners.delete(listener);
  };

  const activate = (expectedRevision = snapshot.revision) => {
    const current = snapshot;
    if (expectedRevision !== current.revision) return Object.freeze({ ok: false, reason: 'stale-snapshot' });
    if (!current.ready) return Object.freeze({ ok: false, reason: 'not-ready' });
    if (current.provider !== 'hulk' && current.provider !== 'native') {
      return Object.freeze({ ok: false, reason: 'unavailable' });
    }
    if (current.disabled || isDisabled(current.control)) return Object.freeze({ ok: false, reason: 'disabled' });
    if (!current.control?.isConnected) return Object.freeze({ ok: false, reason: 'detached-control' });
    current.control.click();
    return Object.freeze({ ok: true, reason: 'delegated', revision: current.revision });
  };

  window.PolyPouchPurchasingAuthority = Object.freeze({
    version: VERSION,
    getSnapshot: () => snapshot,
    subscribe,
    activate,
    refresh: () => scheduleResolve('manual-refresh'),
  });

  document.dispatchEvent(new CustomEvent('polypouch:purchasing-authority-ready'));
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init, { once: true });
  else init();
})();
